import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import employeeAuthService from '../services/employeeAuthService';
import { useAuthStore } from '../store/authStore';

interface PasswordChangeModalProps {
  open: boolean;
  onSuccess: () => void;
  onLogout?: () => void;
  onTryLater?: () => void;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ open, onSuccess, onLogout, onTryLater }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userType } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword) {
      setError(t('passwordChange.errors.currentPasswordRequired'));
      return;
    }

    if (password.length < 8) {
      setError(t('passwordChange.errors.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordChange.errors.passwordNoMatch'));
      return;
    }

    // Basic validation for complexity (optional but recommended)
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setError(t('passwordChange.errors.passwordComplexity'));
      return;
    }

    setLoading(true);
    try {
      const result = userType === 'employee' 
        ? await employeeAuthService.changePassword(currentPassword, password)
        : await authService.changePassword(currentPassword, password);
        
      if (result.success) {
        onSuccess();
      } else {
        setError(result.message || t('passwordChange.errors.changeFailed'));
      }
    } catch (err: any) {
      setError(
        err.message === 'Current password is incorrect'
          ? t('passwordChange.errors.currentPasswordIncorrect')
          : err.message || t('passwordChange.errors.generalError')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={!onTryLater}
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          mb: 1
        }}>
          <Box sx={{
            bgcolor: '#FFFBE3',
            p: 1.5,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #FFD32F'
          }}>
            <LockReset sx={{ color: '#FF690F', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#273B7D' }}>
            {t('passwordChange.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
          {t('passwordChange.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label={t('passwordChange.currentPassword')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label={t('passwordChange.newPassword')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label={t('passwordChange.confirmPassword')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('passwordChange.passwordMustContain')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={t('passwordChange.characters8Plus')}
                  size="small"
                  variant="outlined"
                  color={password.length >= 8 ? "success" : "default"}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                <Chip
                  label={t('passwordChange.uppercase')}
                  size="small"
                  variant="outlined"
                  color={/[A-Z]/.test(password) ? "success" : "default"}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                <Chip
                  label={t('passwordChange.lowercase')}
                  size="small"
                  variant="outlined"
                  color={/[a-z]/.test(password) ? "success" : "success"}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                <Chip
                  label={t('passwordChange.numberSymbol')}
                  size="small"
                  variant="outlined"
                  color={/[\d!@#$%^&*]/.test(password) ? "success" : "default"}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: '#FF690F',
                boxShadow: '0 4px 12px rgba(255, 105, 15, 0.3)',
                '&:hover': {
                  bgcolor: '#E34712',
                },
              }}
            >
              {loading ? t('passwordChange.updating') : t('passwordChange.setNewPassword')}
            </Button>
            
            {onTryLater && (
              <Button
                fullWidth
                variant="outlined"
                onClick={onTryLater}
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  borderColor: '#499FDD',
                  color: '#499FDD',
                  '&:hover': {
                    bgcolor: 'rgba(73, 159, 221, 0.05)',
                    borderColor: '#273B7D',
                    color: '#273B7D',
                  },
                }}
              >
                {t('passwordChange.tryLater')}
              </Button>
            )}
            
            {onLogout && (
              <Button
                fullWidth
                variant="text"
                onClick={onLogout}
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  color: '#EF5350',
                  '&:hover': {
                    bgcolor: 'rgba(239, 83, 80, 0.05)',
                  },
                }}
              >
                {t('passwordChange.logoutButton')}
              </Button>
            )}
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeModal;